import { useSetState } from '@domain-group/react-hooks'

import useCampaign from '@hooks/useCampaign'
import useOrder from '@hooks/useOrder'
import useProviderPriceList from '@hooks/useProviderPriceList'
import usePublication from '@hooks/usePublication'
import usePublicationEdition from '@hooks/usePublicationEdition'
import useSelector from '@hooks/useSelector'
import useServiceCategories from '@hooks/useServiceCategories'

import type { EntitiesState } from '@redux/entities'
import type { PublicationEditionModel, ServiceProductModel } from '@models/types'

type DefaultState = {
  searchString: string,
  selectedCampaignId?: number,
  selectedPriceListId?: number,
  selectedServiceCategoryId?: number,
  selectedServiceProductId?: number,
}

const defaultState: DefaultState = {
  searchString: '',
  selectedCampaignId: undefined,
  selectedPriceListId: undefined,
  selectedServiceCategoryId: undefined,
  selectedServiceProductId: undefined,
}

const sortProducts = (serviceProducts: ServiceProductModel[]) => serviceProducts.sort((productA, productB) => {
  const sortA = productA?.options?.publication
    ? Number(productA.options.publication.grid?.columns) * Number(productA.options.publication.grid?.rows)
    : 0

  const sortB = productB?.options?.publication
    ? Number(productB.options.publication.grid?.columns) * Number(productB.options.publication.grid?.rows)
    : 0

  if (sortA > sortB) return -1
  if (sortA < sortB) return 1
  return 0
})

type SetupOrderPayloadParams = {
  campaignId?: number,
  publicationId?: number,
  providerPriceListItems: EntitiesState['providerPriceListItems'],
  publicationEdition: PublicationEditionModel,
  requestOptions?: object,
  serviceProduct: ServiceProductModel,
}

const setupOrderPayload = (params: SetupOrderPayloadParams) => {
  const {
    campaignId,
    publicationId,
    providerPriceListItems,
    serviceProduct,
    requestOptions,
    publicationEdition,
  } = params

  // @ts-expect-error TS2339
  const priceListItem = providerPriceListItems[serviceProduct.items?.[0]] || {}

  // Array for handle callback actions
  return [{
    campaign_id: campaignId,
    order_type: 'publication',
    item: {
      price_list_item: {
        id: priceListItem?.id,
      },
      publication: {
        id: publicationId,
      },
      publication_editions: [publicationEdition.id],
      product_unlocks: [],
      options: JSON.stringify([]),
      data: JSON.stringify({
        tags: [],
      }),
    },
  }, requestOptions]
}

type UseCreateCampaignPublicationOrderModalOptions = {
  publicationEdition: PublicationEditionModel,
  requestOptions?: object,
}

function useCreateCampaignPublicationOrderModal(options: UseCreateCampaignPublicationOrderModalOptions) {
  const { publicationEdition: initPublicationEdition, requestOptions } = options

  const [state, setState] = useSetState(defaultState)
  const {
    selectedCampaignId,
    selectedPriceListId,
    selectedServiceCategoryId,
    selectedServiceProductId,
  } = state

  const entitiesPayload = useSelector(reduxState => reduxState.entities)
  const {
    providerPriceListItems,
    serviceProducts,
  } = entitiesPayload

  const campaignPayload = useCampaign({ id: selectedCampaignId }, {
    performHttpRequests: !!selectedCampaignId,
    requestOptions: {
      include_campaign_image: true,
    },
  })

  const { campaign } = campaignPayload

  const providerPriceListPayload = useProviderPriceList({ id: selectedPriceListId }, {
    performHttpRequests: !!selectedPriceListId,
    requestOptions: {
      // @ts-expect-error TS2561
      include_provider_price_list_products: true,
      include_provider_price_list_product_items: true,
    },
  })

  const {
    providerPriceList: {
      active_product_ids = [],
    },
  } = providerPriceListPayload

  const publicationEditionPayload = usePublicationEdition(initPublicationEdition)
  const { publicationEdition } = publicationEditionPayload

  const publicationPayload = usePublication({ id: publicationEdition.publication_id })
  const {
    publication,
  } = publicationPayload

  const activeServiceProducts: ServiceProductModel[] = active_product_ids.map(serviceProductId => (
    serviceProducts[serviceProductId]
  )).filter(i => i)

  const filteredServiceProducts = sortProducts(activeServiceProducts.filter((serviceProduct) => {
    const isCompatibleCategory = serviceProduct.categories
      && serviceProduct.categories.includes(Number(selectedServiceCategoryId))

    return (
      serviceProduct.compatible_publications
      && serviceProduct.compatible_publications.includes(publication.id)
      && isCompatibleCategory
    )
  }))

  const serviceCategoriesPayload = useServiceCategories({
    filters: {
      service_key: 'newspaper',
    },
    performHttpRequests: !!selectedCampaignId,
    requestOptions: {
      include_service_category_service: true,
    },
  })

  const orderPayload = useOrder({ agency_id: campaign.agency_id, campaign_id: campaign.id })
  const {
    callbacks: {
      createOrders,
    },
    creating: creatingOrder,
  } = orderPayload

  const serviceProduct = selectedServiceProductId && serviceProducts[selectedServiceProductId]
    ? serviceProducts[selectedServiceProductId] : {}

  const saveEnabled = !!selectedServiceProductId

  return {
    callbacks: {
      createOrders,
      selectServiceCategoryId: (serviceCategoryId: number) => {
        const newspaperPriceListId = publication.price_lists?.newspaper?.id

        setState({
          selectedPriceListId: newspaperPriceListId,
          selectedServiceCategoryId: serviceCategoryId,
        })
      },
      selectServiceProductId: (serviceProductId: number) => {
        setState({
          selectedServiceProductId: serviceProductId,
        })
      },
      setState,
      setupOrderPayload: () => setupOrderPayload({
        campaignId: selectedCampaignId,
        publicationId: publication.id,
        providerPriceListItems,
        publicationEdition,
        requestOptions,
        // @ts-expect-error TS2740
        serviceProduct,
      }),
    },
    campaignPayload,
    creatingOrder,
    filteredServiceProducts,
    providerPriceListPayload,
    saveEnabled,
    serviceCategoriesPayload,
    state,
  }
}

export default useCreateCampaignPublicationOrderModal
